import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { getLocalized, HealthLang } from "../health/healthTypes";
import {
    healthCol,
    healthColStack,
    healthContentPadX,
    healthHeroDesktopLayout,
    healthHeroGrid,
    healthHeroSectionY,
    healthSectionInnerStyle,
    healthSectionOuter,
} from "../health/healthLayout";
import {
    healthFocusVisible,
    healthHeroSubtitle,
    healthHeroTitle,
    healthPrimaryButton,
    healthSecondaryButton,
    healthButtonArrowFa,
    healthButtonLabel,
    healthSectionEyebrow,
    healthHeroSurface,
} from "../health/healthVisualStyles";
import { scrollToHealthHashAnchor } from "../health/healthScroll";
import { educationLocalePath } from "./educationLocalePath";
import { EducationPracticeHeroPanel } from "./EducationPracticeHeroPanel";

interface EducationHeroProps {
    data: DataKeys;
}

export function EducationHero({ data }: EducationHeroProps) {
    const { lang } = useLang();
    const navigate = useNavigate();
    const hLang = lang as HealthLang;
    const visual = (data?.visual as DataKeys) ?? {};
    const titleLead = getLocalized(data?.titleLead, hLang);
    const titleAccent = getLocalized(data?.titleAccent, hLang);
    const titleFallback = getLocalized(data?.title, hLang);
    const primaryHref = educationLocalePath(
        (data?.ctaPrimary?.href as string) ?? "/education/medical-sensei",
        hLang
    );
    const secondaryAnchor = (data?.ctaSecondary?.anchor as string) ?? "#learning-framework";

    return (
        <header className={`${healthSectionOuter} ${healthHeroSurface} max-lg:overflow-visible`}>
            <div className="m-auto w-full" style={healthSectionInnerStyle}>
                <div
                    className={`${healthContentPadX} ${healthHeroSectionY} ${healthHeroDesktopLayout}`}
                >
                    <div className={healthHeroGrid}>
                        <div className={`${healthCol} ${healthColStack}`}>
                            <p className={healthSectionEyebrow}>
                                {getLocalized(data?.eyebrow, hLang)}
                            </p>
                            <h1 className={`${healthHeroTitle} [text-align:left]`}>
                                {titleLead && titleAccent ? (
                                    <>
                                        {titleLead}{" "}
                                        <span className="text-teal-600">{titleAccent}</span>
                                    </>
                                ) : (
                                    titleFallback
                                )}
                            </h1>
                            <p className={`${healthHeroSubtitle} max-w-xl [text-align:left]`}>
                                {getLocalized(data?.subtitle, hLang)}
                            </p>
                            <div className="flex w-full flex-col gap-3 pt-2 max-lg:items-stretch lg:flex-row lg:flex-nowrap lg:items-center lg:gap-4">
                                <Link
                                    to={primaryHref}
                                    className={`${healthPrimaryButton} ${healthFocusVisible} lg:shrink-0`}
                                >
                                    {getLocalized(data?.ctaPrimary?.label, hLang)}
                                </Link>
                                <a
                                    href={secondaryAnchor}
                                    onClick={(event) => {
                                        scrollToHealthHashAnchor(
                                            secondaryAnchor,
                                            event,
                                            true,
                                            (to, options) => navigate(to, options)
                                        );
                                    }}
                                    className={`${healthSecondaryButton} ${healthFocusVisible} lg:shrink-0 lg:whitespace-nowrap`}
                                >
                                    <span className={healthButtonLabel}>
                                        {getLocalized(data?.ctaSecondary?.label, hLang)}
                                    </span>
                                    <FontAwesomeIcon icon={faArrowRight} className={healthButtonArrowFa} />
                                </a>
                            </div>
                        </div>
                        <div className={`${healthCol} flex justify-center px-1 py-2 lg:justify-end lg:px-0 lg:py-0`}>
                            <EducationPracticeHeroPanel visual={visual} lang={hLang} />
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}
